import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { getDateAsUTC } from '../utils/dateUtils';
import { DSIcon } from '../../../base';

import Collapsible from '../Collapsible';
import OffersLabel from '../OffersLabel';
import InfoBanner from '../InfoBanner';

import PaymentInfoSection from './PaymentInfoSection';
import PaymentInfoSectionHeader from './PaymentInfoSectionHeader';
import { discountedPrice } from './utils';

import styles from './PaymentInfo.module.scss';

const DEPOSIT_PROTECTION_EXP_CREATION_DATE = '2024-07-09';

const getTenantPaymentsWithCoverageWithoutOffer = ({
  tenantPaymentsWithCoverage,
  tenantPaymentsWithoutOffer
}) => {
  return {
    ...tenantPaymentsWithCoverage,
    payments: tenantPaymentsWithCoverage?.payments?.map((payment, index) => ({
      ...payment,
      amount: tenantPaymentsWithoutOffer?.[index]
    }))
  };
};

const getMonthlyPricing = ({ tenantPaymentsWithCoverage }) => {
  // If a payment has amount and no days, means it is the whole month
  const fullMonthsToPay = tenantPaymentsWithCoverage?.payments
    ?.filter(payment => payment.amount && !payment.days)
    ?.map(payment => payment.amount);

  // no full months to pay
  if (!fullMonthsToPay?.length) {
    return null;
  }

  const minPriceToPay = Math.min(...fullMonthsToPay) / 100;
  const maxPriceToPay = Math.max(...fullMonthsToPay) / 100;

  return minPriceToPay === maxPriceToPay
    ? minPriceToPay
    : `${minPriceToPay}-${maxPriceToPay}`;
};

const PaymentInfoVariant = ({
  pricing,
  areSectionsExpanded,
  renderLandlordPolicies = null,
  hideDepositElanLaw,
  detailedAdminFee,
  monthlyRentItems,
  downPaymentItem,
  sahFeeItem,
  promoCodeItem,
  depositItem,
  adminFeeItem,
  hasRentCollection,
  reservationPriceWithoutOffer = null,
  creationDate,
  isInstantBooking = false
}) => {
  const hasDeposit = hideDepositElanLaw || !!depositItem.price;
  const monthlyPricing = getMonthlyPricing(pricing);
  const monthlyPricingWithoutOffer = getMonthlyPricing({
    tenantPaymentsWithCoverage:
      getTenantPaymentsWithCoverageWithoutOffer(pricing)
  });
  const isCreatedAfterDepositExperiment = () =>
    !creationDate ||
    getDateAsUTC(new Date(creationDate)) >
      getDateAsUTC(new Date(DEPOSIT_PROTECTION_EXP_CREATION_DATE));

  const hasOffer = !!pricing.offer;
  return (
    <>
      <PaymentInfoSection
        name={
          isInstantBooking
            ? trans('paymentdetails.when-booking-is-accepted.instant-booking')
            : trans('paymentdetails.when-booking-is-accepted')
        }
        price={pricing.reservationPrice}
        priceWithoutOffer={reservationPriceWithoutOffer}
        currencyIsoCode={pricing.currencyIsoCode}
        dataTest="spotahomeBookingPaymentInfo.total"
        items={[downPaymentItem, sahFeeItem, promoCodeItem]}
        isCollapsed={!areSectionsExpanded}
        payToSpotahome
        renderBeforeSection={() => (
          <>
            {hasOffer && (
              <OffersLabel
                appliedOfferValue={discountedPrice({
                  discountType: pricing.offer.discountType,
                  discountValue: pricing.offer.discountValue,
                  currencyCode: pricing.currencyCode
                })}
              />
            )}
          </>
        )}
      />
      <PaymentInfoSection
        dataTest="spotahomeBookingPaymentInfo.move-in"
        name={trans('paymentdetails.when-move-in')}
        price={
          !hideDepositElanLaw &&
          ((detailedAdminFee && detailedAdminFee.totalToPayOnMoveIn) ||
            pricing.adminFee + pricing.securityDeposit)
        }
        currencyIsoCode={pricing.currencyIsoCode}
        items={[depositItem, adminFeeItem]}
        isCollapsed={!areSectionsExpanded}
        payToSpotahome={false}
      />
      {hasDeposit && isCreatedAfterDepositExperiment() ? (
        <InfoBanner
          theme="tertiary"
          closable={false}
          className={styles['payment-info-component__deposit-protection']}
          icon={<DSIcon name="lock_closed" />}
          content={
            <p>
              <span>{trans('listing.labels.deposit-protected.title')}: </span>
              {trans('listing.labels.deposit-protected.description')}
            </p>
          }
        />
      ) : null}
      <PaymentInfoSection
        dataTest="spotahomeBookingPaymentInfo.monthly"
        name={trans('paymentdetails.monthly')}
        price={monthlyPricing}
        priceWithoutOffer={monthlyPricingWithoutOffer}
        currencyIsoCode={pricing.currencyIsoCode}
        items={monthlyRentItems}
        isCollapsed={!areSectionsExpanded && monthlyPricing}
        payToSpotahome={hasRentCollection}
        priceIsMonthly
      />
      {renderLandlordPolicies && (
        <Collapsible
          isControlled={false}
          hasIcon
          isCollapsed={!areSectionsExpanded}
          renderHeader={() => (
            <PaymentInfoSectionHeader
              dataTest={'spotahomeBookingPaymentInfo.landlordPolicies'}
              name={trans('paymentdetails.landlord-policies')}
            />
          )}
          renderBody={renderLandlordPolicies}
        />
      )}
    </>
  );
};

PaymentInfoVariant.propTypes = {
  pricing: PropTypes.shape({
    currencyIsoCode: PropTypes.string,
    reservationPrice: PropTypes.number,
    reservationPriceWithoutFormat: PropTypes.number,
    adminFee: PropTypes.number,
    securityDeposit: PropTypes.string,
    tenantPaymentsWithoutOffer: PropTypes.arrayOf(PropTypes.number),
    tenantPaymentsWithCoverage: PropTypes.shape({
      payments: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.number,
          days: PropTypes.number
        })
      )
    }),
    offer: PropTypes.shape({
      discountType: PropTypes.string,
      discountValue: PropTypes.number
    }),
    currencyCode: PropTypes.string
  }).isRequired,
  hasRentCollection: PropTypes.bool.isRequired,
  areSectionsExpanded: PropTypes.bool.isRequired,
  hideDepositElanLaw: PropTypes.bool.isRequired,
  renderLandlordPolicies: PropTypes.func,
  detailedAdminFee: PropTypes.shape({
    totalToPayOnMoveIn: PropTypes.number
  }).isRequired,
  monthlyRentItems: PropTypes.arrayOf(
    PropTypes.shape({
      alreadyPaid: PropTypes.bool,
      dataTest: PropTypes.string,
      discountType: PropTypes.string,
      discountValue: PropTypes.number,
      name: PropTypes.string,
      price: PropTypes.number,
      priceLabel: PropTypes.string,
      priceWithoutOffer: PropTypes.number,
      tooltip: PropTypes.string
    })
  ).isRequired,
  downPaymentItem: PropTypes.shape({
    dataTest: PropTypes.string,
    discountType: PropTypes.string,
    discountValue: PropTypes.number,
    hint: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    priceWithoutOffer: PropTypes.number,
    tooltip: PropTypes.string
  }).isRequired,
  sahFeeItem: PropTypes.shape({
    dataTest: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    show: PropTypes.bool,
    tooltip: PropTypes.string
  }).isRequired,
  promoCodeItem: PropTypes.shape({
    dataTest: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    showprice: PropTypes.bool
  }).isRequired,
  depositItem: PropTypes.shape({
    dataTest: PropTypes.string,
    details: PropTypes.string,
    hidePrice: PropTypes.bool,
    name: PropTypes.string,
    price: PropTypes.number,
    tooltip: PropTypes.string
  }).isRequired,
  adminFeeItem: PropTypes.shape({
    dataTest: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    show: PropTypes.bool,
    tooltip: PropTypes.string
  }).isRequired,
  reservationPriceWithoutOffer: PropTypes.number,
  creationDate: PropTypes.string.isRequired,
  isInstantBooking: PropTypes.bool
};

export default PaymentInfoVariant;
